import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { MessageCircleWarning } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useBugReport } from "@/hooks/api/useBugReport";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function BugReportButton() {
  const { t } = useTranslation(["common"]);
  const { prepareReport, submitBugReport, isSubmitting } = useBugReport();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [description, setDescription] = useState("");
  const MAX_CHARS = 500;

  const handleButtonClick = async () => {
    await prepareReport();
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    const success = await submitBugReport(description);
    if (success) {
      setDescription("");
      setIsDialogOpen(false);
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setDescription("");
  };

  return (
    <>
      <div className="group" data-bug-report="true">
        <button
          onClick={() => void handleButtonClick()}
          className="flex items-center gap-2 h-10 rounded-full px-3
            bg-red-100 hover:bg-red-200 text-red-800
            dark:bg-red-950/80 dark:hover:bg-red-900 dark:text-red-200
            border border-red-300 dark:border-red-800/50
            shadow-md
            transition-all duration-300 ease-in-out
            w-10 group-hover:w-auto overflow-hidden"
          aria-label={t("common:bugReport.buttonLabel")}
        >
          <MessageCircleWarning className="w-4 h-4 shrink-0" />
          <span className="whitespace-nowrap text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pr-1">
            {t("common:bugReport.buttonLabel")}
          </span>
        </button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md" data-bug-report="true">
          <DialogHeader>
            <DialogTitle>{t("common:bugReport.dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("common:bugReport.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder={t("common:bugReport.placeholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              className="min-h-[120px]"
              maxLength={MAX_CHARS}
              autoFocus
            />
            <p className={`text-xs text-right ${description.length >= MAX_CHARS ? "text-destructive" : "text-muted-foreground"}`}>
              {MAX_CHARS - description.length} / {MAX_CHARS}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              {t("common:buttons.cancel")}
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={isSubmitting || !description.trim()}
            >
              {isSubmitting ? (
                <>
                  <Spinner className="size-4" />
                  {t("common:buttons.loading")}
                </>
              ) : (
                t("common:bugReport.submitButton")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
